import React from 'react'
import ReactDOM from 'react-dom/client'
import installSafeLocalStorage from './shared/safeLocalStorage'
import './index.css'
import App from './App'
import { AuthenticationContextProvider } from './Auth/authentication.context'
import { BrandingContextProvider } from './contexts/BrandingContext'
import * as Sentry from '@sentry/react'
import ErrorFallback from './components/shared/ErrorFallback'

// Make sure localStorage is safe to use before any component (notably the
// AuthenticationContextProvider) touches it. In browsers that block storage,
// window.localStorage can be null and a direct read crashes the whole app.
installSafeLocalStorage()

// Distinguish traces to our own API from third-party calls, so tracing headers
// are attached to the former only.
//
// This was previously the string `/^https:${IP}/`, which is a regex literal
// inside a template literal, not a regex. Sentry treats a string entry as a
// substring match, and no URL contains that text, so the API was never
// recognised and no trace was ever propagated to the backend.
const apiUrl = process.env.REACT_APP_API_URL
const tracePropagationTargets = apiUrl
  ? [new URL(apiUrl, window.location.origin).origin]
  : []

// 0..1. Both defaults keep the behaviour that was hard-coded here. Tracing and
// profiling every session is what exhausts a Sentry quota, so set
// REACT_APP_SENTRY_TRACES_SAMPLE_RATE well below 1 on production.
const rate = (name, fallback) => {
  const parsed = Number.parseFloat(process.env[name])
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback
}

Sentry.init({
  dsn:
    process.env.REACT_APP_SENTRY_DSN ||
    'https://e24b9d56c70cb8878783cb4bfb9bb630@o4504542497275904.ingest.us.sentry.io/4508086744776704',

  // Staging and production reported into one undifferentiated stream, so an
  // error could not be attributed to an environment.
  environment:
    process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development',
  // Ties a regression to the deploy that introduced it. Optional.
  release: process.env.REACT_APP_SENTRY_RELEASE || undefined,

  tracesSampleRate: rate('REACT_APP_SENTRY_TRACES_SAMPLE_RATE', 1.0),
  tracePropagationTargets,
  replaysSessionSampleRate: rate('REACT_APP_SENTRY_REPLAYS_SAMPLE_RATE', 0.1),
  replaysOnErrorSampleRate: rate('REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE', 1.0),
})

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  // The fallback used to be a bare `<p>An error has occurred</p>`: a render
  // error anywhere left a blank page with no way out but a manual reload.
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <ErrorFallback error={error} resetError={resetError} />
    )}
  >
    <AuthenticationContextProvider>
      <BrandingContextProvider>
        <App />
      </BrandingContextProvider>
    </AuthenticationContextProvider>
  </Sentry.ErrorBoundary>
)
